<template>
  <div></div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { useEventsStore, type SSEEvent } from '../../stores/events-store'
import { useAuthStore } from '../../stores/auth-store'

const eventsStore = useEventsStore()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { init: showToast } = useToast()

let unsubscribe: (() => void) | null = null

const buildPreview = (content: string | undefined, messageType: string | undefined) => {
  if (content && content.trim().length > 0) {
    return content.length > 120 ? `${content.slice(0, 120)}…` : content
  }
  if (messageType && messageType !== 'text') {
    return `[${messageType}]`
  }
  return 'New message'
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const handleNewMessage = (event: SSEEvent) => {
  const data = event.data || {}
  const chat = data.Chat
  const message = data.Message
  const user = data.User

  if (!chat || !message || !user) return

  // Ignore when the agent is currently on the Events page
  if (route.name === 'events') return

  // Only show if the chat is assigned to the current logged-in agent
  const currentAgentId = authStore.user?.id
  if (!currentAgentId || chat.AgentAspId !== currentAgentId) return

  // Only show for messages coming from the end-user
  if (message.SenderType !== 'user') return

  const userId = user.Id?.toString()
  if (!userId) return

  // Skip if the agent is already viewing this user's chat
  if (route.name === 'chat' && route.query.userId === userId) return

  const preview = buildPreview(message.Content, message.MessageType)
  const title = 'New message'
  const messageHtml = user.FullName
    ? `<strong>${escapeHtml(user.FullName)}</strong>: ${escapeHtml(preview)}`
    : escapeHtml(preview)

  showToast({
    title,
    message: messageHtml,
    // let the app/theme handle the main background color so the toast matches site styles
    // (we control accents via CSS below)
    duration: 7000,
    closeable: true,
    position: 'top-right',
    dangerouslyUseHtmlString: true,
    onClick: () => {
      router.push({ name: 'chat', query: { userId } })
    },
  })
}

onMounted(() => {
  unsubscribe = eventsStore.addEventListener('new_message', handleNewMessage)
})

onBeforeUnmount(() => {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
})
</script>

<style lang="scss" scoped>
:deep(.va-toast) {
  cursor: pointer;
  /* use Vuestic / app theme tokens so the toast visually matches the rest of the UI */
  background: var(--va-background-element, #ffffff);
  color: var(--va-text-primary, #0f172a);
  border-left: 4px solid var(--va-primary);
  box-shadow: 0 6px 18px rgba(2, 6, 23, 0.06);

  .va-toast__title {
    color: var(--va-text-primary, #0f172a);
    font-weight: 600;
  }
}
</style>
